import { useCallback, useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './Button';
import { useOptionalConfirm } from './ConfirmProvider';
import { isTopDialog } from './dialog-stack';
import { useDialogA11y } from './useDialogA11y';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** Nom accessible quand il n'y a pas de `title` visible. */
  ariaLabel?: string;
  children: React.ReactNode;
  /** Pied sur mesure. Avec `onSubmit`, omis : le pied standard est rendu. */
  footer?: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * Mode formulaire : le corps et le pied sont enveloppés dans un `<form>` —
   * Entrée valide, le bouton principal est de type `submit`, la validation
   * native du navigateur est désactivée (les erreurs sont celles de l'app).
   */
  onSubmit?: () => void | Promise<unknown>;
  /** Libellé du bouton principal du pied standard (défaut : « Enregistrer »). */
  submitLabel?: string;
  /** Libellé du bouton d'annulation du pied standard (défaut : « Annuler »). */
  cancelLabel?: string;
  submitDisabled?: boolean;
  submitLoading?: boolean;
  submitVariant?: 'primary' | 'danger';
  /** Contenu ajouté à gauche du pied standard (case à cocher, lien d'aide…). */
  footerStart?: React.ReactNode;
  /**
   * Des saisies non enregistrées existent : fermer (Échap, croix, fond,
   * Annuler) demande d'abord confirmation au lieu de tout perdre en silence.
   */
  dirty?: boolean;
}

const sizeStyles = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export function Modal({
  open,
  onClose,
  title,
  ariaLabel,
  children,
  footer,
  className,
  size = 'md',
  onSubmit,
  submitLabel,
  cancelLabel,
  submitDisabled,
  submitLoading,
  submitVariant = 'primary',
  footerStart,
  dirty,
}: ModalProps) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const confirm = useOptionalConfirm();

  const dialogId = useDialogA11y(open, panelRef);

  // Fermeture demandée (Échap, fond, croix, Annuler) : garde « modifications
  // non enregistrées » quand le formulaire est sale.
  const requestClose = useCallback(async () => {
    if (dirty && confirm && !submitLoading) {
      const discard = await confirm({
        title: t('common.unsavedTitle'),
        message: t('common.unsavedMessage'),
        confirmLabel: t('common.discard'),
        danger: true,
      });
      if (!discard) return;
    }
    onClose();
  }, [dirty, confirm, submitLoading, onClose, t]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Seul le dialogue du dessus se ferme ; pendant un envoi on ne ferme pas.
      if (e.key === 'Escape' && isTopDialog(dialogId) && !submitLoading) void requestClose();
    }
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, requestClose, dialogId, submitLoading]);

  if (!open) return null;

  const standardFooter = onSubmit && !footer;
  const footerNode = standardFooter ? (
    <>
      {footerStart && <div className="mr-auto flex items-center gap-2">{footerStart}</div>}
      <Button type="button" variant="outline" onClick={() => void requestClose()} disabled={submitLoading}>
        {cancelLabel ?? t('common.cancel')}
      </Button>
      <Button
        type="submit"
        variant={submitVariant}
        loading={submitLoading}
        disabled={submitDisabled}
      >
        {submitLabel ?? t('common.save')}
      </Button>
    </>
  ) : (
    footer
  );

  const content = (
    <>
      <div className="flex-1 overflow-y-auto scrollbar-thin px-6 py-5">{children}</div>
      {footerNode && (
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border px-6 py-4">
          {footerNode}
        </div>
      )}
    </>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/30 animate-fade-in"
        onClick={() => void requestClose()}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : ariaLabel}
        tabIndex={-1}
        className={cn(
          // max-h + colonne flex : le corps scrolle en interne au lieu de
          // déborder d'un viewport verrouillé (body overflow:hidden).
          'relative flex max-h-[calc(100dvh-2rem)] w-full flex-col rounded bg-surface shadow-modal animate-scale-in border border-border outline-none',
          sizeStyles[size],
          className,
        )}
      >
        {title && (
          <div data-dialog-header className="flex shrink-0 items-center justify-between border-b border-border px-6 py-4">
            <h2 id={titleId} className="text-base font-semibold text-text">{title}</h2>
            <button
              type="button"
              onClick={() => void requestClose()}
              aria-label={t('common.close')}
              className="rounded p-1.5 text-subtle transition-colors hover:bg-surface-subtle hover:text-text"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        {onSubmit ? (
          <form
            noValidate
            className="flex min-h-0 flex-1 flex-col"
            onSubmit={(e) => {
              e.preventDefault();
              if (submitLoading || submitDisabled) return;
              void onSubmit();
            }}
          >
            {content}
          </form>
        ) : (
          content
        )}
      </div>
    </div>
  );
}
